import React from "react";
import { useStepperContext } from "./StepperContext";
import { CheckIcon } from "lucide-react";
import { Step } from "./types";
import { useLanguage } from "@/contexts/LanguageContext";

interface StepperHeaderProps {
  steps: Step[];
  activeStep: number;
  orientation?: "horizontal" | "vertical";
  variant?: "default" | "outlined" | "contained";
  size?: "sm" | "md" | "lg";
}

export const StepperHeader: React.FC<StepperHeaderProps> = ({
  steps,
  activeStep,
  orientation = "horizontal",
  variant = "default",
  size = "md",
}) => {
  const { setActiveStep, isStepOptional, isStepCompleted, isStepDisabled } = useStepperContext();
  const { t } = useLanguage();

  const sizeClasses = {
    sm: "w-6 h-6 text-xs",
    md: "w-8 h-8 text-sm",
    lg: "w-10 h-10 text-base",
  };

  const variantClasses = {
    default: "border-2",
    outlined: "border-2",
    contained: "shadow-sm",
  };

  return (
    <div
      className={`flex ${orientation === "vertical" ? "flex-col gap-4" : "flex-row items-center"} mb-6`}
    >
      {steps.map((step, index) => {
        const isActive = index === activeStep;
        const isCompleted = isStepCompleted(index);
        const isOptional = isStepOptional(index);
        const isDisabled = isStepDisabled(index);

        const handleStepClick = () => {
          if (!isDisabled && index !== activeStep) {
            setActiveStep(index);
          }
        };

        return (
          <React.Fragment key={step.id}>
            <div
              className={`
                ${
                  orientation === "vertical"
                    ? "flex gap-4 items-center"
                    : "flex flex-col items-center justify-center"
                } 
                ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              `}
            >
                <button
                  type="button"
                  onClick={handleStepClick}
                  disabled={isDisabled}
                  className={`
                    ${sizeClasses[size]} 
                    ${variantClasses[variant]}
                    flex items-center justify-center rounded-full
                    transition-colors
                    ${
                      isCompleted
                        ? "bg-teal-500 border-teal-500 text-white"
                        : isActive
                        ? "bg-white border-teal-500 text-teal-500 shadow-lg"
                        : "bg-gray-100 border-gray-300 text-gray-400"
                    }
                    ${!isDisabled && "hover:shadow-md"}
                  `}
                  aria-current={isActive ? "step" : undefined}
                >
                  {isCompleted ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : (
                    <span className={`font-semibold ${isActive ? "text-teal-500" : ""}`}>{index + 1}</span>
                  )}
                </button>

                <div className="flex flex-col mt-2 text-center relative">
                  <span
                    className={`
                      text-sm font-medium transition-colors
                      ${isActive ? "text-teal-700" : isCompleted ? "text-teal-700" : "text-gray-500"}
                    `}
                  >
                    {step.title}
                  </span>

                  {step.description && <span className="text-xs text-gray-500 mt-1">{step.description}</span>}

                  {isOptional && <span className="text-xs text-gray-400 italic mt-1">({t("common.optional")})</span>}
                  
                  {/* Active step indicator - positioned absolutely to not affect layout */}
                  {isActive && (
                    <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-teal-500 rounded-full"></div>
                  )}
                </div>
              </div>
            {/* Connector Line (for horizontal) */}
            {index !== steps.length - 1 && orientation === "horizontal" && (
              <div className="flex-1 px-4">
                <div
                  className={`
                    h-0.5 w-full 
                    ${isCompleted || index < activeStep ? "bg-teal-500" : "bg-gray-200"}
                  `}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
